/**
 * AdminMessages — the Browse Page Messages console.
 *
 * Features
 * -------
 * • List of up to 5 announcements (any status).
 * • Drag-and-drop reorder via @dnd-kit.
 * • Create + Edit modal with React-Quill rich-text editor.
 * • Live preview matching the exact Browse banner look.
 * • Toggle active / delete inline.
 *
 * The backend enforces the 5-message cap and sanitizes HTML with bleach.
 * We also sanitize with DOMPurify inside <SplashMessagePreview> before render.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVertical, Eye, EyeSlash, PencilSimple, Plus, Trash, X } from "@phosphor-icons/react";

import { api, formatErr } from "@/lib/api";
import { SplashMessagePreview } from "@/components/SplashMessageBox";

const MAX = 5;

const QUILL_MODULES = {
  toolbar: [
    [{ header: [3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike", "color", "background",
  "list", "link",
];

function emptyDraft() {
  return {
    id: null,
    title: "",
    content: "",
    cta_text: "",
    cta_link: "",
    cta_new_tab: false,
    is_active: true,
  };
}

function SortableRow({ msg, onEdit, onDelete, onToggleActive }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: msg.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`admin-msg-row-${msg.id}`}
      className="h57-surface p-3 flex items-center gap-3"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`admin-msg-drag-${msg.id}`}
        className="cursor-grab active:cursor-grabbing text-zinc-500 hover:text-white"
        aria-label="Drag to reorder"
      >
        <DotsSixVertical size={20} weight="bold" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[10px] text-zinc-500">#{(msg.display_order ?? 0) + 1}</span>
          <span className="font-display font-black text-sm text-white truncate">{msg.title}</span>
          {msg.is_active
            ? <span className="h57-chip h57-chip-orange !py-0.5 !px-1.5 !text-[9px]">ACTIVE</span>
            : <span className="h57-chip !py-0.5 !px-1.5 !text-[9px] opacity-60">HIDDEN</span>}
        </div>
        <div
          className="mt-1 text-xs text-zinc-400 line-clamp-1"
          dangerouslySetInnerHTML={{ __html: msg.content }}
        />
        {msg.cta_text && (
          <div className="mt-1 font-mono text-[10px] text-zinc-500">
            CTA: <span className="text-[#FFD700]">{msg.cta_text}</span> → {msg.cta_link}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1 sm:flex-row shrink-0">
        <button
          type="button"
          onClick={() => onToggleActive(msg)}
          data-testid={`admin-msg-toggle-${msg.id}`}
          className="h57-chip !py-1 !px-2 !text-[10px] hover:!border-[#FF5F1F]"
          title={msg.is_active ? "Hide from Browse" : "Show on Browse"}
        >
          {msg.is_active ? <><EyeSlash size={12} weight="bold" /> Hide</> : <><Eye size={12} weight="bold" /> Show</>}
        </button>
        <button
          type="button"
          onClick={() => onEdit(msg)}
          data-testid={`admin-msg-edit-${msg.id}`}
          className="h57-chip !py-1 !px-2 !text-[10px] hover:!border-[#FFD700] hover:!text-[#FFD700]"
        >
          <PencilSimple size={12} weight="bold" /> Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(msg)}
          data-testid={`admin-msg-delete-${msg.id}`}
          className="h57-chip !py-1 !px-2 !text-[10px] !border-red-500/50 !text-red-400 hover:!border-red-500 hover:!text-red-300"
        >
          <Trash size={12} weight="bold" /> Delete
        </button>
      </div>
    </div>
  );
}

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/announcements");
      setMessages(data.messages || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    if (messages.length >= MAX) {
      toast.error(`You already have ${MAX} messages. Delete one to add a new one.`);
      return;
    }
    setDraft(emptyDraft());
    setDrafting(true);
  };

  const startEdit = (m) => {
    setDraft({
      id: m.id,
      title: m.title || "",
      content: m.content || "",
      cta_text: m.cta_text || "",
      cta_link: m.cta_link || "",
      cta_new_tab: !!m.cta_new_tab,
      is_active: !!m.is_active,
    });
    setDrafting(true);
  };

  const save = async () => {
    if (!draft.title.trim()) { toast.error("Title is required."); return; }
    const plain = (draft.content || "").replace(/<[^>]+>/g, "").trim();
    if (!plain) { toast.error("Message content cannot be empty."); return; }
    if ((draft.cta_text || draft.cta_link) && !(draft.cta_text && draft.cta_link)) {
      toast.error("A button needs both text and a link.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        content: draft.content,
        cta_text: draft.cta_text.trim() || null,
        cta_link: draft.cta_link.trim() || null,
        cta_new_tab: !!draft.cta_new_tab,
        is_active: !!draft.is_active,
      };
      if (draft.id) {
        await api.patch(`/admin/announcements/${draft.id}`, payload);
        toast.success("Message updated");
      } else {
        await api.post("/admin/announcements", payload);
        toast.success("Message created");
      }
      setDrafting(false);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admin/announcements/${m.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/admin/announcements/${m.id}`, { is_active: !m.is_active });
      toast.success(m.is_active ? "Hidden" : "Now showing on Browse");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = messages.findIndex((m) => m.id === active.id);
    const newIndex = messages.findIndex((m) => m.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(messages, oldIndex, newIndex).map((m, i) => ({ ...m, display_order: i }));
    setMessages(next); // optimistic
    try {
      await api.post("/admin/announcements/reorder", { ids: next.map((m) => m.id) });
    } catch (e) {
      toast.error(formatErr(e));
      load();
    }
  };

  const previewMessage = useMemo(() => ({
    id: "preview",
    title: draft.title || "Untitled",
    content: draft.content || "<p><em>Start typing to see the preview…</em></p>",
    cta_text: draft.cta_text || null,
    cta_link: draft.cta_link || null,
    cta_new_tab: !!draft.cta_new_tab,
  }), [draft]);

  return (
    <div className="mt-6 space-y-4" data-testid="admin-messages-panel">
      <div className="h57-surface p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Browse Page Messages</div>
            <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
              Rich-text announcements that rotate on the top of the Browse page every 8 seconds.
              You can have up to <span className="text-white font-semibold">{MAX}</span> messages,
              and toggle them active/hidden without deleting.
            </p>
          </div>
          <button
            type="button"
            onClick={startCreate}
            disabled={messages.length >= MAX}
            data-testid="admin-msg-new"
            className="h57-btn-primary !py-2 !px-3 text-xs disabled:opacity-40"
          >
            <Plus size={14} weight="bold" className="inline mr-1" /> New Message
          </button>
        </div>
        <div className="mt-3 font-mono text-[10px] text-zinc-500 tracking-widest uppercase">
          {messages.length} / {MAX} used · drag the handle to reorder
        </div>
      </div>

      {loading ? (
        <div className="h57-surface p-8 text-center text-zinc-400 text-sm">Loading…</div>
      ) : messages.length === 0 ? (
        <div className="h57-surface p-8 text-center text-zinc-400 text-sm">
          No messages yet. Click <span className="text-[#FF5F1F]">New Message</span> to create one.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={messages.map((m) => m.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2" data-testid="admin-msg-list">
              {messages.map((m) => (
                <SortableRow
                  key={m.id}
                  msg={m}
                  onEdit={startEdit}
                  onDelete={remove}
                  onToggleActive={toggleActive}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {drafting && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-msg-modal">
          <div className="h57-surface max-w-3xl w-full my-4 p-5 relative">
            <button
              type="button"
              onClick={() => setDrafting(false)}
              data-testid="admin-msg-modal-close"
              className="absolute top-3 right-3 text-zinc-500 hover:text-white"
              aria-label="Close"
            >
              <X size={20} weight="bold" />
            </button>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">
              → {draft.id ? "Edit Message" : "New Message"}
            </div>

            <div className="mt-4 space-y-4">
              <div>
                <label className="h57-label">Internal Title</label>
                <input
                  className="h57-input"
                  data-testid="admin-msg-title-input"
                  value={draft.title}
                  maxLength={120}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="e.g. Site Maintenance Notice"
                />
                <div className="mt-1 font-mono text-[9px] text-zinc-500 uppercase tracking-widest">
                  Shown as the small label above the message on Browse.
                </div>
              </div>

              <div>
                <label className="h57-label">Message Body</label>
                <div className="h57-quill">
                  <ReactQuill
                    theme="snow"
                    value={draft.content}
                    onChange={(html) => setDraft({ ...draft, content: html })}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Write your announcement…"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="h57-label">Button Text (optional)</label>
                  <input
                    className="h57-input"
                    data-testid="admin-msg-cta-text-input"
                    value={draft.cta_text}
                    maxLength={60}
                    onChange={(e) => setDraft({ ...draft, cta_text: e.target.value })}
                    placeholder="e.g. Learn More"
                  />
                </div>
                <div>
                  <label className="h57-label">Button Link</label>
                  <input
                    className="h57-input"
                    data-testid="admin-msg-cta-link-input"
                    value={draft.cta_link}
                    maxLength={500}
                    onChange={(e) => setDraft({ ...draft, cta_link: e.target.value })}
                    placeholder="/about  or  https://…"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-zinc-300 select-none" data-testid="admin-msg-newtab-label">
                  <input
                    type="checkbox"
                    checked={draft.cta_new_tab}
                    onChange={(e) => setDraft({ ...draft, cta_new_tab: e.target.checked })}
                    data-testid="admin-msg-newtab"
                  />
                  Open link in new tab
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-zinc-300 select-none" data-testid="admin-msg-active-label">
                  <input
                    type="checkbox"
                    checked={draft.is_active}
                    onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                    data-testid="admin-msg-active"
                  />
                  Show on Browse (active)
                </label>
              </div>

              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">→ Live Preview</div>
                <SplashMessagePreview message={previewMessage} testId="admin-msg-preview" />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDrafting(false)}
                data-testid="admin-msg-cancel"
                className="h57-btn-secondary !py-2 !px-3 text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving}
                data-testid="admin-msg-save"
                className="h57-btn-primary !py-2 !px-3 text-xs disabled:opacity-40"
              >
                {saving ? "Saving…" : (draft.id ? "Save Changes" : "Create Message")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
